import React from 'react';

/**
 * 서버 사이드에서도 사용할 수 있는 안전한 HTML 파서
 * 
 * 정규식을 사용하여 HTML을 파싱하고 React 요소로 변환합니다.
 * XSS 공격을 방지하기 위해 허용된 태그와 속성만 처리하며,
 * 위험한 CSS 속성과 JavaScript 코드는 차단합니다.
 * 
 * 주요 기능:
 * - HTML 문자열을 안전하게 파싱
 * - 허용된 태그와 속성만 처리
 * - XSS 방지를 위한 보안 검증
 * - 표 구조 자동 수정 (tbody 추가)
 * - React 요소로 변환
 */

/**
 * 허용된 HTML 태그 및 속성 정의
 * 
 * 보안을 위해 제한된 태그와 속성만 허용하며, 
 * 필요에 따라 추가 태그를 등록할 수 있습니다.
 */
const ALLOWED_TAGS = [
  'p', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'hr',
  'strong', 'b', 'em', 'i', 'u', 'del', 's', 'mark',
  'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'br'
];

/**
 * 각 태그별로 허용되는 HTML 속성 목록
 * 
 * 보안을 위해 각 태그마다 허용할 속성을 명시적으로 정의합니다.
 * href, src 등 URL 관련 속성은 보안 검증을 거쳐야 합니다.
 */
const ALLOWED_ATTRIBUTES: Record<string, string[]> = {
  'a': ['href', 'target', 'rel', 'title'],
  'img': ['src', 'alt', 'title', 'class', 'style'],
  'code': ['class'],
  'pre': ['class'],
  'table': ['class', 'style', 'width', 'cellspacing', 'cellpadding'],
  'thead': ['class', 'style'],
  'tbody': ['class', 'style'],
  'tr': ['class', 'style'],
  'th': ['class', 'style', 'width', 'colspan', 'rowspan'],
  'td': ['class', 'style', 'width', 'colspan', 'rowspan'],
  'div': ['class', 'style'],
  'span': ['class', 'style', 'data-color'],
  'p': ['class', 'style'],
  'h1': ['class', 'style'],
  'h2': ['class', 'style'],
  'h3': ['class', 'style'],
  'h4': ['class', 'style'],
  'h5': ['class', 'style'],
  'h6': ['class', 'style'],
  'ul': ['class', 'style'],
  'ol': ['class', 'style'],
  'li': ['class', 'style'],
  'blockquote': ['class', 'style'],
  'strong': ['class', 'style'],
  'em': ['class', 'style'],
  'del': ['class', 'style'],
  'u': ['class', 'style'],
  'mark': ['class', 'style', 'data-color']
};

/**
 * HTML 파싱 핵심 함수들
 * 
 * HTML 문자열을 안전하게 파싱하고 React 요소로 변환하는 핵심 기능을 제공합니다.
 */
export function parseHtml(html: string): HtmlNode[] {
  if (!html) return [];

  const nodes: HtmlNode[] = [];
  let currentIndex = 0;

  while (currentIndex < html.length) {
    const char = html[currentIndex];

    if (char === '<') {
      // 태그 시작 - 정규식으로 태그 정보 추출
      const tagMatch = html.slice(currentIndex).match(/^<(\/?)([a-zA-Z][a-zA-Z0-9]*)([^>]*)>/);
      if (tagMatch) {
        const [fullMatch, isClosing, tagName, attributesStr] = tagMatch;
        const lowerTagName = tagName.toLowerCase();

        // 허용된 태그만 처리
        if (ALLOWED_TAGS.includes(lowerTagName)) {
          if (isClosing) {
            // 닫는 태그 처리
            nodes.push({ type: 'closing', tag: lowerTagName });
          } else {
            // 여는 태그 처리 - 속성 파싱 포함
            const attributes = parseAttributes(attributesStr, lowerTagName);
            nodes.push({
              type: 'opening',
              tag: lowerTagName,
              attributes,
              selfClosing: isSelfClosingTag(lowerTagName)
            });
          }
          currentIndex += fullMatch.length;
          continue;
        }
      }

      // 태그가 아닌 '<' 문자는 텍스트로 처리
      // 다음 '<' 문자를 찾아서 그 사이의 내용을 텍스트로 처리
      const nextLtIndex = html.indexOf('<', currentIndex + 1);
      const nextGtIndex = html.indexOf('>', currentIndex + 1);

      // '<' 다음에 '>'가 오는 경우 (태그처럼 보이지만 허용되지 않은 태그)
      // 또는 '>'가 '<'보다 먼저 오는 경우 (실제 텍스트의 '<')
      if (nextGtIndex !== -1 && (nextLtIndex === -1 || nextGtIndex < nextLtIndex)) {
        const textContent = html.substring(currentIndex, nextGtIndex + 1);
        nodes.push({ type: 'text', content: textContent });
        currentIndex = nextGtIndex + 1;
        continue;
      }
    }

    // 텍스트 노드 처리 - 태그가 아닌 모든 문자를 텍스트로 처리
    let textContent = '';
    while (currentIndex < html.length && html[currentIndex] !== '<') {
      textContent += html[currentIndex];
      currentIndex++;
    }

    // 공백만 있는 텍스트는 제외 (줄바꿈 포함 원본 유지)
    if (textContent !== '') {
      nodes.push({ type: 'text', content: textContent });
    }
  }

  return nodes;
}

/**
 * HTML 속성을 파싱하는 함수
 * 
 * 태그의 속성 문자열을 파싱하여 객체로 변환합니다.
 * 허용된 속성만 처리하며, 보안 검증을 거쳐 안전한 속성만 반환합니다.
 * 
 * @param attributesStr - 파싱할 속성 문자열 (예: 'class="title" style="color: red"')
 * @param tagName - 속성이 속한 태그 이름 (허용된 속성 확인용)
 * @returns 파싱된 속성 객체
 * 
 * @example
 * ```typescript
 * const attrs = parseAttributes('class="title" style="color: red"', 'div');
 * console.log(attrs); // { class: 'title', style: 'color: red' }
 * ```
 */
function parseAttributes(attributesStr: string, tagName: string): Record<string, string> {
  const attributes: Record<string, string> = {};
  const allowedAttrs = ALLOWED_ATTRIBUTES[tagName] || [];

  // 속성 정규식 매칭: name="value" 형태의 속성 추출
  const attrRegex = /([a-zA-Z][a-zA-Z0-9-]*)\s*=\s*["']([^"']*)["']/g;
  let match;

  while ((match = attrRegex.exec(attributesStr)) !== null) {
    const [, attrName, attrValue] = match;

    // 허용된 속성인지 확인
    if (allowedAttrs.includes(attrName)) {
      // 보안 검증을 통과한 속성만 추가
      if (isAttributeSafe(attrName, attrValue)) {
        attributes[attrName] = attrValue;
      }
    }
  }

  return attributes;
}

/**
 * 보안 검증 함수들
 * 
 * HTML 속성과 CSS 스타일의 안전성을 검증하여 보안을 보장하는 함수들을 제공합니다.
 */

/**
 * HTML 속성이 안전한지 검증하는 함수
 * 
 * XSS 공격을 방지하기 위해 위험한 속성값을 차단합니다.
 * 특히 href, src 등 URL 관련 속성과 style 속성은 엄격하게 검증합니다.
 * 
 * @param attrName - 검증할 속성 이름
 * @param attrValue - 검증할 속성 값
 * @returns 안전 여부 (true: 안전, false: 위험)
 * 
 * @example
 * ```typescript
 * const isSafe = isAttributeSafe('href', 'https://example.com');
 * console.log(isSafe); // true
 * 
 * const isDangerous = isAttributeSafe('href', 'javascript:alert(1)');
 * console.log(isDangerous); // false
 * ```
 */
function isAttributeSafe(attrName: string, attrValue: string): boolean {
  if (attrName === 'href' || attrName === 'src') {
    // URL 검증 - 허용된 프로토콜과 경로만 허용
    return attrValue.startsWith('http://') ||
      attrValue.startsWith('https://') ||
      attrValue.startsWith('/') ||
      attrValue.startsWith('#');
  }

  if (attrName === 'target') {
    // target 속성은 _blank만 허용 (보안상 안전)
    return attrValue === '_blank';
  }

  if (attrName === 'style') {
    // style 속성 보안 검증 - CSS 인젝션 방지
    return isStyleSafe(attrValue);
  }

  if (attrName === 'data-color') {
    // TipTap 색상 확장의 data-color 속성 검증 - 16진수 색상 코드만 허용
    return /^#[0-9A-Fa-f]{6}$/.test(attrValue);
  }

  if (attrName === 'width' || attrName === 'height') {
    // 크기 속성 검증 - 숫자 + 단위 또는 퍼센트만 허용
    return /^(\d+(\.\d+)?(px|em|rem|%|vw|vh)?|auto)$/i.test(attrValue);
  }

  if (attrName === 'colspan' || attrName === 'rowspan') {
    // 테이블 셀 병합 속성 검증 - 양의 정수만 허용
    return /^\d+$/.test(attrValue) && parseInt(attrValue) > 0;
  }

  if (attrName === 'cellspacing' || attrName === 'cellpadding') {
    // 테이블 간격 속성 검증 - 숫자 + 단위 또는 퍼센트만 허용
    return /^(\d+(\.\d+)?(px|em|rem|%)?)$/i.test(attrValue);
  }

  // 기타 속성은 허용 (class, title 등)
  return true;
}

/**
 * CSS 스타일이 안전한지 검증하는 함수
 * 
 * CSS 인젝션 공격을 방지하기 위해 위험한 CSS 속성을 차단합니다.
 * 특히 JavaScript 실행을 유도할 수 있는 속성과 고정 위치 스타일을 제한합니다.
 * 
 * @param styleValue - 검증할 CSS 스타일 문자열
 * @returns 안전 여부 (true: 안전, false: 위험)
 * 
 * @example
 * ```typescript
 * const isSafe = isStyleSafe('color: red; font-size: 16px;');
 * console.log(isSafe); // true
 * 
 * const isDangerous = isStyleSafe('position: fixed; z-index: 999999;');
 * console.log(isDangerous); // false
 * ```
 */
function isStyleSafe(styleValue: string): boolean {
  // 위험한 CSS 속성 차단 목록
  const dangerousProperties = [
    'expression', 'javascript:', 'vbscript:', 'onload', 'onerror',
    'position: fixed', 'position:fixed', 'z-index: 999999', 'z-index:999999'
  ];

  const lowerStyle = styleValue.toLowerCase();

  // 위험한 속성이 포함되어 있는지 확인
  for (const dangerous of dangerousProperties) {
    if (lowerStyle.includes(dangerous)) {
      return false;
    }
  }

  // 허용된 CSS 속성만 사용 가능 (색상 관련 속성 추가)
  const allowedProperties = [
    'width', 'height', 'min-width', 'max-width', 'min-height', 'max-height',
    'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
    'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
    'border', 'border-width', 'border-style', 'border-color', 'border-radius',
    'background', 'background-color', 'background-image', 'background-size', 'background-position', 'background-repeat',
    'color', 'font-size', 'font-weight', 'font-family', 'text-align',
    'display', 'visibility', 'opacity', 'overflow', 'text-decoration',
    'line-height', 'letter-spacing', 'word-spacing', 'text-transform',
    'vertical-align', 'white-space', 'word-wrap', 'word-break',
    // 색상 관련 추가 속성들
    'background-color', 'border-color', 'outline-color'
  ];

  // CSS 속성 추출 및 검증
  const cssProperties = styleValue.split(';').map(rule => rule.trim().split(':')[0]?.trim());

  for (const prop of cssProperties) {
    if (prop && !allowedProperties.some(allowed => prop.toLowerCase().includes(allowed))) {
      return false;
    }
  }

  return true;
}

/**
 * 자체 닫힘 태그인지 확인하는 함수
 * 
 * HTML에서 닫는 태그가 필요 없는 태그들을 식별합니다.
 * 
 * @param tagName - 확인할 태그 이름
 * @returns 자체 닫힘 여부 (true: 자체 닫힘, false: 닫는 태그 필요)
 */
function isSelfClosingTag(tagName: string): boolean {
  return ['br', 'hr', 'img'].includes(tagName);
}

/**
 * 타입 정의
 * 
 * HTML 파싱과 React 변환에 필요한 타입들을 정의합니다.
 */

/**
 * HTML 노드 타입 정의
 * 
 * 파싱된 HTML을 표현하는 노드 객체의 구조를 정의합니다.
 * 각 노드는 여는 태그, 닫는 태그, 텍스트 중 하나의 타입을 가집니다.
 */
export interface HtmlNode {
  type: 'opening' | 'closing' | 'text';
  tag?: string;
  attributes?: Record<string, string>;
  content?: string;
  selfClosing?: boolean;
}

/**
 * React 요소 변환 함수들
 * 
 * 파싱된 HTML 노드들을 React 요소로 변환하는 함수들을 제공합니다.
 */

/**
 * 파싱된 노드들을 React 요소로 변환하는 함수
 * 
 * HTML 노드 배열을 React 컴포넌트로 변환합니다.
 * 스택 기반 알고리즘을 사용하여 중첩된 HTML 구조를 올바르게 처리합니다.
 * 
 * @param nodes - 파싱된 노드 배열
 * @returns React 요소 배열
 * 
 * @example
 * ```typescript
 * const nodes = parseHtml('<div><p>Hello</p></div>');
 * const elements = nodesToReactElements(nodes);
 * // React 컴포넌트로 렌더링 가능
 * ```
 */
export function nodesToReactElements(nodes: HtmlNode[]): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  const stack: Array<{ tag: string; attributes: Record<string, string>; children: React.ReactNode[]; key: string }> = [];
  let elementCounter = 0;

  // 표 구조 수정을 위한 전처리
  const processedNodes = fixTableStructure(nodes);

  for (let i = 0; i < processedNodes.length; i++) {
    const node = processedNodes[i];

    if (node.type === 'text') {
      // 텍스트 노드 처리
      const textContent = node.content ?? '';
      const textParts = textContent.split('\n');
      const textNodes: React.ReactNode[] = [];

      textParts.forEach((part, index) => {
        if (part !== '') {
          textNodes.push(part);
        }
        if (index < textParts.length - 1) {
          textNodes.push(
            React.createElement('br', {
              key: `br-${elementCounter++}-${i}-${index}`,
            })
          );
        }
      });

      if (stack.length > 0) {
        // 스택에 있는 태그의 자식으로 추가
        stack[stack.length - 1].children.push(...textNodes);
      } else {
        // 최상위 텍스트
        elements.push(...textNodes);
      }
    } else if (node.type === 'opening') {
      if (node.selfClosing) {
        // 자체 닫힘 태그 (br, hr, img 등)
        const key = `self-${node.tag}-${elementCounter++}-${i}`;
        const element = createReactElement(node.tag!, node.attributes || {}, [], key);
        if (stack.length > 0) {
          stack[stack.length - 1].children.push(element);
        } else {
          elements.push(element);
        }
      } else {
        // 여는 태그를 스택에 추가
        const key = `open-${node.tag}-${elementCounter++}-${i}`;
        stack.push({
          tag: node.tag!,
          attributes: node.attributes || {},
          children: [],
          key
        });
      }
    } else if (node.type === 'closing') {
      // 닫는 태그 처리 - 스택에서 꺼내서 React 요소 생성
      if (stack.length > 0 && stack[stack.length - 1].tag === node.tag) {
        const { tag, attributes, children, key } = stack.pop()!;
        const element = createReactElement(tag, attributes, children, key);

        if (stack.length > 0) {
          stack[stack.length - 1].children.push(element);
        } else {
          elements.push(element);
        }
      }
    }
  }

  return elements;
}

/**
 * 표 구조 수정 함수들
 * 
 * HTML 표의 구조를 올바르게 수정하여 React에서 렌더링할 수 있도록 합니다.
 */

/**
 * 표 구조를 수정하는 함수 - tbody가 없는 경우 자동으로 추가
 * 
 * HTML 표에서 tbody 태그가 누락된 경우를 자동으로 감지하고
 * tr 태그들을 tbody로 감싸서 올바른 표 구조를 만듭니다.
 * 
 * @param nodes - 원본 노드 배열
 * @returns 수정된 노드 배열
 * 
 * @example
 * ```typescript
 * // <table><tr><td>Hello</td></tr></table>
 * // ↓
 * // <table><tbody><tr><td>Hello</td></tr></tbody></table>
 * ```
 */
function fixTableStructure(nodes: HtmlNode[]): HtmlNode[] {
  const result: HtmlNode[] = [];
  let i = 0;

  while (i < nodes.length) {
    const node = nodes[i];

    if (node.type === 'opening' && node.tag === 'table') {
      result.push(node);
      i++;

      // table 내부의 tr 태그들을 찾아 tbody로 감싸기
      const tableContent: HtmlNode[] = [];
      let tableDepth = 1;
      let hasExplicitTbody = false;
      let hasDirectTr = false;

      // table의 내용을 수집
      while (i < nodes.length && tableDepth > 0) {
        const innerNode = nodes[i];

        if (innerNode.type === 'opening' && innerNode.tag === 'table') {
          tableDepth++;
        } else if (innerNode.type === 'closing' && innerNode.tag === 'table') {
          tableDepth--;
          if (tableDepth === 0) {
            break; // table 끝
          }
        } else if (tableDepth === 1) {
          // 직접적인 table의 자식 요소들
          if (innerNode.type === 'opening' && innerNode.tag === 'tbody') {
            hasExplicitTbody = true;
          } else if (innerNode.type === 'opening' && innerNode.tag === 'tr') {
            hasDirectTr = true;
          }
        }

        tableContent.push(innerNode);
        i++;
      }

      // tbody가 없고 직접적인 tr이 있는 경우 tbody로 감싸기
      if (!hasExplicitTbody && hasDirectTr) {
        const wrappedContent = wrapDirectTrWithTbody(tableContent);
        result.push(...wrappedContent);
      } else {
        result.push(...tableContent);
      }

      // table 닫는 태그 추가
      if (i < nodes.length && nodes[i].type === 'closing' && nodes[i].tag === 'table') {
        result.push(nodes[i]);
        i++;
      }
    } else {
      result.push(node);
      i++;
    }
  }

  return result;
}

/**
 * 직접적인 tr 태그들을 tbody로 감싸는 함수
 * 
 * table 내부의 tr 태그들을 tbody 태그로 감싸서
 * 올바른 HTML 표 구조를 만듭니다.
 * 
 * @param tableContent - table 내부 노드들
 * @returns tbody로 감싸진 노드들
 */
function wrapDirectTrWithTbody(tableContent: HtmlNode[]): HtmlNode[] {
  const result: HtmlNode[] = [];
  let i = 0;
  let tbodyStarted = false;

  while (i < tableContent.length) {
    const node = tableContent[i];

    // thead, tfoot은 그대로 유지
    if (node.type === 'opening' && (node.tag === 'thead' || node.tag === 'tfoot')) {
      // tbody가 시작되었다면 먼저 닫기
      if (tbodyStarted) {
        result.push({ type: 'closing', tag: 'tbody' });
        tbodyStarted = false;
      }

      result.push(node);
      i++;

      // thead/tfoot 내용을 그대로 복사
      let depth = 1;
      while (i < tableContent.length && depth > 0) {
        const innerNode = tableContent[i];
        if (innerNode.type === 'opening' && innerNode.tag === node.tag) {
          depth++;
        } else if (innerNode.type === 'closing' && innerNode.tag === node.tag) {
          depth--;
        }
        result.push(innerNode);
        i++;
      }
      continue;
    }

    // 직접적인 tr 태그 발견
    if (node.type === 'opening' && node.tag === 'tr') {
      if (!tbodyStarted) {
        result.push({ type: 'opening', tag: 'tbody', attributes: {} });
        tbodyStarted = true;
      }
    }

    // tbody가 이미 있는 경우
    if (node.type === 'opening' && node.tag === 'tbody') {
      tbodyStarted = false; // 명시적 tbody이므로 우리가 추가한 tbody는 사용하지 않음
    }

    result.push(node);
    i++;
  }

  // tbody가 시작되었다면 닫기
  if (tbodyStarted) {
    result.push({ type: 'closing', tag: 'tbody' });
  }

  return result;
}

/**
 * React 요소 생성 및 스타일 처리 함수들
 * 
 * HTML 노드를 React 요소로 변환하고 CSS 스타일을 처리하는 함수들을 제공합니다.
 */

/**
 * React 요소를 생성하는 함수
 * 
 * HTML 태그 정보를 바탕으로 React 요소를 생성합니다.
 * HTML 속성을 React 속성으로 변환하고 class를 className으로 매핑합니다.
 * 
 * @param tag - 태그 이름
 * @param attributes - HTML 속성들
 * @param children - 자식 요소들
 * @param key - React key prop
 * @returns React 요소
 */
function createReactElement(
  tag: string,
  attributes: Record<string, string>,
  children: React.ReactNode[],
  key?: string
): React.ReactElement {
  // HTML 속성을 React 속성으로 변환
  const reactProps: Record<string, string | Record<string, string>> = {};

  Object.entries(attributes).forEach(([key, value]) => {
    // class 속성을 className으로 변환
    if (key === 'class') {
      reactProps['className'] = value;
    } else if (key === 'style') {
      // style 속성을 React 스타일 객체로 변환
      reactProps['style'] = parseStyleString(value);
    } else if (key === 'data-color') {
      // TipTap 색상 확장의 data-color 속성을 style로 변환
      const existingStyle = reactProps['style'] as Record<string, string> || {};
      reactProps['style'] = { ...existingStyle, color: value };
    } else if (key === 'src') {
      // 이미지 src 속성에 대한 특별 처리
      // /uploads/images/ 경로로 시작하는 경우 API 라우트를 통해 접근
      if (value.startsWith('/uploads/images/')) {
        const filename = value.split('/').pop();
        reactProps[key] = `/api/uploads/images/${filename}`;
      } else {
        reactProps[key] = value;
      }
    } else {
      reactProps[key] = value;
    }
  });

  // React.createElement를 사용하여 요소 생성
  const props = key ? { ...reactProps, key } : reactProps;
  return React.createElement(tag, props, ...children);
}

/**
 * CSS 스타일 문자열을 React 스타일 객체로 변환하는 함수
 * 
 * CSS 스타일 문자열을 React에서 사용할 수 있는 스타일 객체로 변환합니다.
 * CSS 속성명을 camelCase로 변환하여 React 스타일 규칙을 준수합니다.
 * 
 * @param styleString - CSS 스타일 문자열 (예: "width: 100px; color: red")
 * @returns React 스타일 객체
 * 
 * @example
 * ```typescript
 * const styleObj = parseStyleString('width: 100px; color: red;');
 * console.log(styleObj); // { width: '100px', color: 'red' }
 * ```
 */
function parseStyleString(styleString: string): Record<string, string> {
  const styleObj: Record<string, string> = {};

  if (!styleString || typeof styleString !== 'string') {
    return styleObj;
  }

  // 세미콜론으로 분리하여 각 스타일 속성 처리
  const styles = styleString.split(';').filter(style => style.trim());

  styles.forEach(style => {
    const colonIndex = style.indexOf(':');
    if (colonIndex > 0) {
      const property = style.slice(0, colonIndex).trim();
      const value = style.slice(colonIndex + 1).trim();

      if (property && value) {
        // CSS 속성명을 camelCase로 변환 (kebab-case -> camelCase)
        const camelCaseProperty = property.replace(/-([a-z])/g, (match, letter) =>
          letter.toUpperCase()
        );

        // 색상 값 정규화 (16진수 색상 코드가 3자리인 경우 6자리로 확장)
        let normalizedValue = value;
        if (camelCaseProperty === 'color' || camelCaseProperty === 'backgroundColor' || camelCaseProperty === 'borderColor') {
          // #RGB 형태를 #RRGGBB 형태로 변환
          if (/^#[0-9A-Fa-f]{3}$/.test(value)) {
            normalizedValue = value.replace(/^#([0-9A-Fa-f])([0-9A-Fa-f])([0-9A-Fa-f])$/, '#$1$1$2$2$3$3');
          }
        }

        styleObj[camelCaseProperty] = normalizedValue;
      }
    }
  });

  return styleObj;
} 
